import styled from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import { Flex } from '~/ui/flex/Flex';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { QUIZ_INSTA_SURVEY } from '../itemIds';
import { QuizProvider } from '../quiz/QuizStore';
import { QuizInstaHeader } from './QuizInsta';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import { Bar } from '~/training/progress-bar/ProgressBar.styles';

interface Props {
  id: string;
  name: string;
  picture: string;
}

const StyledQuizInstaSurveyWrapper = styled.div<{
  isVideoFinished: boolean;
  visualFeedback: boolean;
}>`
  display: inline-block;
  position: relative;

  .phone-background {
    pointer-events: none;
    z-index: 2;
  }

  .instaCard {
    display: ${p => (p.isVideoFinished ? 'flex' : 'none')};

    @keyframes slideInFromBottom {
      0% {
        transform: translateY(100%);
        opacity: 0;
      }
      100% {
        transform: translateY(0);
        opacity: 1;
      }
    }

    padding-top: 15px;
    flex-direction: column;
    text-align: center;
    position: absolute;
    height: fit-content;
    bottom: 40px;
    z-index: 2;
    animation: ${p => (p.visualFeedback ? '0.7s' : '0s')} ease-out 0s 1
      slideInFromBottom;
  }

  .quizInstaBar {
    margin-left: 5px;
    position: absolute;
    display: block;
    z-index: 2;
    bottom: 69px;
    width: 95%;
  }

  .flatGauge {
    width: 200px;
    height: 50px;

    .flatGaugeButton {
      font-size: 24px;
      line-height: 46px;
    }
  }
`;

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback');

const selector = (s: MediaPlayerStore) => select(s, 'progress');

const QuizInstaSurveyWrapper: React.FC = ({ children }) => {
  const [isVideoFinished, setVideoFinished] = useState(false);
  const { progress } = useMediaPlayerStore(selector);
  const { visualFeedback } = useTrainingStore(selectorVisualFeedback);

  useEffect(() => {
    if (!isVideoFinished && progress === 1) {
      setVideoFinished(true);
    }
  }, [progress]);

  return (
    <StyledQuizInstaSurveyWrapper
      aria-live="polite"
      visualFeedback={visualFeedback}
      isVideoFinished={isVideoFinished}
    >
      {children}
      {!isVideoFinished && (
        <Bar className="quizInstaBar">
          <Bar bg="white" opacity="0.4" />
          <Bar
            bg="white"
            w={`${(progress * 100).toString().slice(0, 4)}%`}
            marginTop="-4px"
          />
        </Bar>
      )}
    </StyledQuizInstaSurveyWrapper>
  );
};

export type QuizInstaSurveyDefinition = ItemDefinition<
  typeof QUIZ_INSTA_SURVEY,
  Props
>;

export class QuizInstaSurvey extends Item<typeof QUIZ_INSTA_SURVEY, Props> {
  protected render({ id, name, picture, children }: RenderProps<Props>) {
    return (
      <QuizProvider quizId={id || 'default'}>
        <Flex variant="center" position="relative" my="md">
          <QuizInstaSurveyWrapper>
            <QuizInstaHeader name={name} picture={picture} />
            {children}
          </QuizInstaSurveyWrapper>
        </Flex>
      </QuizProvider>
    );
  }
}

registry[QUIZ_INSTA_SURVEY] = QuizInstaSurvey;
